import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiTrash2, FiUser, FiMail, FiShield } from 'react-icons/fi';
import './AdminUsersPage.css';

const API_URL = '/api';

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name || !formData.email || !formData.password) {
      setError('Tous les champs sont requis');
      return;
    }

    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/auth/create-user`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Utilisateur créé avec succès');
      setFormData({ name: '', email: '', password: '' });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création');
    }
  };

  const handleToggleAdmin = async (userId, currentAdmin) => {
    if (!window.confirm(`${currentAdmin ? 'Retirer' : 'Attribuer'} les droits administrateur à cet utilisateur ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/auth/users/${userId}/admin`, 
        { is_admin: !currentAdmin },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(`Droits admin ${!currentAdmin ? 'attribués' : 'retirés'}`);
      loadUsers();
    } catch (err) {
      setError('Erreur lors de la mise à jour des droits');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/auth/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Utilisateur supprimé avec succès');
      loadUsers();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  return (
    <div className="admin-page">
      <h2>👥 Gestion des Utilisateurs</h2>

      <div className="admin-content">
        {/* Formulaire d'ajout */}
        <div className="card add-user-section">
          <h3>Ajouter un nouvel utilisateur</h3>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleAddUser} className="admin-form">
            <div className="form-group">
              <label>Nom complet *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Jean Dupont"
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="jean@example.com"
              />
            </div>

            <div className="form-group">
              <label>Mot de passe *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
              />
            </div>

            <button type="submit" className="btn btn-primary">
              Créer l'utilisateur
            </button>
          </form>
        </div>

        {/* Liste des utilisateurs */}
        <div className="card users-list-section">
          <h3>Utilisateurs existants ({users.length})</h3>

          {loading ? (
            <p className="loading-text">⏳ Chargement...</p>
          ) : users.length === 0 ? (
            <p className="empty-text">Aucun utilisateur</p>
          ) : (
            <div className="users-table">
              {users.map(user => (
                <div key={user.id} className="user-item">
                  <div className="user-info">
                    <div className="user-name">
                      <FiUser /> {user.name}
                    </div>
                    <div className="user-email">
                      <FiMail /> {user.email}
                    </div>
                    {user.is_admin && (
                      <div className="user-admin-badge">
                        <FiShield /> Admin
                      </div>
                    )}
                  </div>
                  <div className="user-actions">
                    <button
                      className={`btn-admin ${user.is_admin ? 'btn-admin-active' : ''}`}
                      onClick={() => handleToggleAdmin(user.id, user.is_admin)}
                      title={user.is_admin ? 'Retirer admin' : 'Attribuer admin'}
                    >
                      <FiShield />
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteUser(user.id)}
                      title="Supprimer"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
